import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AlertTriangle, ExternalLink } from 'lucide-react';

export default function PatientHeader({ patient, encounter, compact = false }) {
  const navigate = useNavigate();
  if (!patient) return null;

  const name = `${patient.name?.first || ''} ${patient.name?.last || ''}`;
  const age = patient.dob ? Math.floor((Date.now() - new Date(patient.dob).getTime()) / 31557600000) : '?';
  const allergies = patient.allergies || [];
  const payer = patient.insurance?.payer_name || 'Unknown';
  const payerClass = patient.insurance?.payer_class || 'unknown';

  return (
    <div className="flex items-center gap-4 px-4 sm:px-6 py-3 bg-card border-b sticky top-0 z-20" data-testid="patient-header">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
          {patient.name?.first?.charAt(0)}{patient.name?.last?.charAt(0)}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold truncate" style={{ fontFamily: 'Space Grotesk' }}>{name}</h2>
            <span className="text-xs text-muted-foreground tabular-nums">MRN: {patient.mrn}</span>
          </div>
          {!compact && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
              <span>{age}yo {patient.sex}</span>
              <span>DOB: {patient.dob}</span>
              <span>{payer} ({payerClass.replace('_', ' ')})</span>
            </div>
          )}
        </div>
      </div>

      {/* Allergies */}
      {allergies.length > 0 && (
        <div className="flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
          {allergies.slice(0, 2).map((a, i) => (
            <Badge key={i} variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
              {a.name}
            </Badge>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => navigate(`/chart/${patient.id}`)} data-testid="patient-header-open-chart">
        <ExternalLink className="w-3 h-3" /> Chart
      </Button>
    </div>
  );
}
